import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles, faqs, siteInfo } from '@/mocks/data';

const featuredArticles = articles.slice(0, 4);

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteInfo.fullName,
  url: siteInfo.url,
  description: siteInfo.description,
  inLanguage: 'zh-CN',
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteInfo.name,
  url: siteInfo.url,
  description: siteInfo.description,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-9999',
    contactType: 'customer service',
    availableLanguage: 'Chinese',
  },
};

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: siteInfo.fullName,
  description: siteInfo.description,
  url: siteInfo.url,
  isPartOf: { '@id': `${siteInfo.url}#website` },
  about: { '@id': `${siteInfo.url}#organization` },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Navbar />
      <ScrollToTop />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20abstract%20flowing%20white%20liquid%20texture%20with%20smooth%20organic%20curves%2C%20minimalist%20artistic%20composition%2C%20warm%20neutral%20cream%20and%20ivory%20tones%2C%20soft%20ethereal%20lighting%2C%20fine%20art%20photography%2C%20subtle%20gradients%2C%20clean%20modern%20aesthetic&width=1800&height=900&seq=baijiang-hero-01&orientation=landscape"
            alt="白浆 - 传统工艺与现代应用"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              白浆 — 传统工艺与现代应用全解析
            </h1>
            <p className="text-sm md:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed">
              白浆是一种广泛应用于建筑、造纸、食品及工业领域的白色浆状物料。深入探索白浆的原料组成、制备工艺、性能特点及选购指南，找到最适合您需求的白浆产品方案。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <Link
                to="/category"
                className="px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                探索产品分类
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-background-50/20 backdrop-blur-sm text-white border border-white/30 rounded-md font-medium text-sm hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Overview */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                白浆产品分类
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                涵盖建筑、造纸、食品及工业四大领域，每种白浆均有独特的配方体系与应用场景
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category?tab=${cat.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-950 mb-2 text-base" style={{ fontFamily: "var(--font-heading)" }}>
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed line-clamp-3">
                    {cat.description}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-sm text-primary-500 font-medium">
                    <span>了解更多</span>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-16 px-4 md:px-6 bg-secondary-50">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '500+', label: '配方方案' },
              { value: '4', label: '应用领域' },
              { value: '20+', label: '行业经验（年）' },
              { value: '99.8%', label: '客户满意度' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl md:text-3xl font-bold text-accent-500" style={{ fontFamily: "var(--font-heading)" }}>
                  {stat.value}
                </div>
                <div className="text-xs text-foreground-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                  精选文章
                </h2>
                <p className="text-sm text-foreground-600">深度了解白浆在各个领域的专业知识与应用技巧</p>
              </div>
              <Link
                to="/category"
                className="flex items-center gap-1 text-sm text-primary-500 font-medium hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>查看全部</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded text-xs font-medium mb-2">
                      {article.categoryName}
                    </span>
                    <h3 className="font-semibold text-foreground-950 text-sm mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed mb-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.publishedAt}</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">关于白浆的常见疑问，这里都有答案</p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="font-medium text-sm text-foreground-950 pr-4">{faq.question}</span>
                    <i
                      className={`text-foreground-500 text-lg transition-transform flex-shrink-0 ${
                        openFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed border-t border-background-200/70 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-16 px-4 md:px-6 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20white%20and%20cream%20flowing%20gradient%20texture%2C%20minimalist%20elegant%20background%2C%20warm%20neutral%20tones%2C%20fine%20art%20style%2C%20clean%20composition&width=1800&height=600&seq=baijiang-cta-01&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background-50/70"></div>
          <div className="relative z-10 text-center max-w-xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              寻找专业的白浆解决方案？
            </h2>
            <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
              无论您是建筑商、造纸企业还是食品生产商，我们都能提供匹配您需求的优质白浆产品方案。
            </p>
            <Link
              to="/category"
              className="inline-flex px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              立即浏览产品
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}